/**
 * Marketing → Reactivación: groups of the leads in Reactivar by pain and by
 * objection (tags written by the IA from the setter's notes). Keys in sync
 * with supabase/functions/reactivation_insights/insights.ts.
 */

export const PAIN_LABELS: Record<string, string> = {
  microplastics: "Microplásticos y contaminantes",
  limescale: "Cal",
  taste: "Sabor del agua del grifo",
  health: "Salud: niños, bebé, mayores",
  carrying: "Cargar garrafas / comodidad",
  plastic: "Plástico y medio ambiente",
  saving: "Ahorro frente al agua embotellada",
  cooking: "Cocinar, café e infusiones",
  alkaline: "Agua alcalina o hidrogenada",
  cold_water: "Quiere agua fría",
};

export const OBJECTION_LABELS: Record<string, string> = {
  price: "Precio / ahora no puede",
  partner: "Lo decide la pareja o la familia",
  rental: "Prefiere renting o alquiler",
  well: "Agua de pozo o fuente",
  space: "Instalación: no cabe, alquiler…",
  trust: "Desconfía (agua muerta, minerales…)",
  compare: "Compara con otras empresas",
  timing: "No es el momento",
  unreachable: "No hemos llegado a hablar",
  not_interested: "Dijo que no le interesa",
  other: "Otra",
};

export type GroupKind = "pain" | "objection";

export interface Insight {
  client_id: number;
  pains: string[];
  objection?: string | null;
  how_to?: string | null;
  quote?: string | null;
  warmth?: string | null;
  analyzed_at: string;
}

export interface GroupMember<T> {
  lead: T;
  insight: Insight;
}

export interface ReactivationGroup<T> {
  kind: GroupKind;
  key: string;
  label: string;
  members: GroupMember<T>[];
  warmth: { alta: number; media: number; baja: number };
}

export const groupLabel = (kind: GroupKind, key: string) =>
  (kind === "pain" ? PAIN_LABELS : OBJECTION_LABELS)[key] ?? key;

const WARMTH_ORDER: Record<string, number> = { alta: 0, media: 1, baja: 2 };

/** Groups of one kind, biggest first; inside, the warmest first. */
export const buildGroups = <T extends { id: number | string }>(
  leads: T[],
  insights: Insight[],
  kind: GroupKind,
): ReactivationGroup<T>[] => {
  const byClient = new Map(insights.map((row) => [String(row.client_id), row]));
  const groups = new Map<string, ReactivationGroup<T>>();
  for (const lead of leads) {
    const insight = byClient.get(String(lead.id));
    if (!insight) continue;
    const keys =
      kind === "pain"
        ? insight.pains
        : insight.objection
          ? [insight.objection]
          : [];
    for (const key of keys) {
      let group = groups.get(key);
      if (!group) {
        group = {
          kind,
          key,
          label: groupLabel(kind, key),
          members: [],
          warmth: { alta: 0, media: 0, baja: 0 },
        };
        groups.set(key, group);
      }
      group.members.push({ lead, insight });
      const warmth = (insight.warmth ?? "media") as keyof typeof group.warmth;
      if (warmth in group.warmth) group.warmth[warmth] += 1;
    }
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      members: [...group.members].sort(
        (a, b) =>
          (WARMTH_ORDER[a.insight.warmth ?? "media"] ?? 1) -
          (WARMTH_ORDER[b.insight.warmth ?? "media"] ?? 1),
      ),
    }))
    .sort((a, b) => b.members.length - a.members.length);
};
